import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Users, HeartHandshake, Flag, Target, Compass, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";

const FLAG_CROWD = "https://images.pexels.com/photos/7044342/pexels-photo-7044342.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600";
const EAGLE = "https://images.unsplash.com/photo-1497119146420-012f8fc80a3a?auto=format&fit=crop&w=1200&q=80";
const VOLUNTEERS = "/by-the-numbers.png";
const CREED_POSTER = "https://customer-assets-39nsmqrw.emergentagent.net/job_defense-corps-1/artifacts/sl0sxemk_PPUSA%20Banner%201.png";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1 } }),
};

const values = [
  { icon: Shield, title: "Duty", text: "We answer the call before it's asked. Preparedness is a responsibility we carry with pride." },
  { icon: Users, title: "Unity", text: "Strength is built shoulder to shoulder. Every member matters, every chapter counts." },
  { icon: HeartHandshake, title: "Service", text: "We show up for our neighbors — in crisis, in need, and in the everyday work of community." },
  { icon: Flag, title: "Honor", text: "We act with integrity and respect for the Constitution, the law, and one another." },
];

export default function About() {
  return (
    <div>
      <Navbar />

      <PageHero
        image={FLAG_CROWD}
        imageAlt="Citizens with American flag"
        kicker="About Patriot Power USA"
        title={<>Patriots Standing Ready To <span className="text-[#D90429]">Serve!</span></>}
        subtitle="We are a nationwide political patriot organization of military combat veterans, former law enforcement & true red blooded everyday American Patriots committed to the protection and defense of families, children, grandchildren and our communities and nation. We love our blessed Country of The United States of America and we will do whatever it takes to protect and keep our country safe and secure. We are trained, organized, united and ready to protect your freedom — we stand ready to serve."
      />

      {/* CREED POSTER */}
      <section className="bg-[#0A1128] text-white py-20 sm:py-28 grain stars tactical-grid relative overflow-hidden" data-testid="about-creed-poster">
        <div className="absolute top-1/4 -left-40 w-[520px] h-[520px] glow-red pointer-events-none" />
        <div className="max-w-4xl mx-auto px-5 sm:px-8 relative z-10 text-center">
          <span className="kicker-pill mx-auto">Our Charge</span>
          <h2 className="font-display text-4xl sm:text-5xl uppercase mt-5 leading-[0.95]">
            Stand. Remember. <span className="text-[#D90429]">Endure.</span>
          </h2>
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mt-10 inline-block relative"
          >
            <div className="absolute -inset-1 bg-gradient-to-br from-[#D90429] to-[#F4A261] opacity-70" aria-hidden="true" />
            <img
              src={CREED_POSTER}
              alt="Patriot Power USA — Stand Up. Look at the flag. Remember who you are."
              className="relative w-full max-w-[560px] h-auto shadow-2xl"
              loading="lazy"
            />
          </motion.div>
        </div>
      </section>

      {/* STORY */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <span className="text-sm uppercase tracking-[0.2em] font-semibold text-[#D90429]">Who We Are</span>
            <h2 className="font-display text-4xl sm:text-5xl uppercase mt-4 leading-[0.95]">Citizens First. Always.</h2>
            <p className="text-lg text-slate-600 mt-6 leading-relaxed">
              Patriot Power USA was founded on a simple belief: a free nation is defended not only by
              its armed forces, but by informed, trained, and united citizenry. We exist to build
              resilient communities capable of protecting themselves and each other.
            </p>
            <p className="text-lg text-slate-600 mt-4 leading-relaxed">
              From Citizen Protection, Protection Against Islamic Terror, Jewish Protection, Armed Public & Private School Security, Church Armed Security, Neighborhood Watch Coordination and
              Veteran Support, Our Members Put Principle Into Action — Every Single Day, In Every
              Corner Of The Country.
            </p>
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={1} className="relative">
            <img src={EAGLE} alt="Bald eagle" className="w-full h-[440px] object-cover" />
            <div className="absolute -bottom-5 -left-5 bg-[#D90429] text-white p-6 hidden sm:block">
              <Compass className="w-8 h-8" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-[#0A1128] text-white py-24 grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
          <h2 className="font-display text-4xl sm:text-6xl uppercase mb-14 leading-[0.95]">Our <span className="text-[#D90429]">Core</span> Values</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <motion.div key={v.title} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i} data-testid="value-card" className="border border-white/15 p-8 hover:border-[#D90429] transition-colors duration-200">
                <v.icon className="w-9 h-9 text-[#D90429] mb-5" strokeWidth={1.75} />
                <h3 className="font-display text-2xl uppercase mb-2">{v.title}</h3>
                <p className="text-white/60 leading-relaxed text-sm">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS + IMAGE */}
      <section className="grid lg:grid-cols-2">
        <div className="relative min-h-[400px]">
          <img src={VOLUNTEERS} alt="Soldiers holding the American flag" className="absolute inset-0 w-full h-full object-cover object-center" />
        </div>
        <div className="bg-muted flex items-center py-20 px-8 sm:px-16">
          <div>
            <Target className="w-10 h-10 text-[#D90429] mb-6" />
            <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95]">By The Numbers</h2>
            <div className="grid grid-cols-2 gap-8 mt-8">
              {[["50", "States Covered"], ["12K+", "Active Members"], ["207+", "Local Chapters"], ["24/7", "Ready to Deploy To Protect You!"]].map(([n, l]) => (
                <div key={l} data-testid="about-stat">
                  <div className="font-display text-5xl text-[#D90429]">{n}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mt-1">{l}</div>
                </div>
              ))}
            </div>
            <Link to="/join" data-testid="about-join-button" className="inline-flex items-center gap-2 mt-10 bg-[#0A1128] text-white px-8 py-4 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#1a2547] transition-colors duration-200 active:scale-95">
              Join Our Newsletter <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
